import React from 'react';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  useTheme,
} from '@mui/material';
import AlertDialog from '../AlertDialog';
import analytics from '../../utils/analytics';

interface CannotCompleteVerificationDialogProps {
  onUnemployedClick: () => Promise<void>;
  onManualEvaluationClick: () => Promise<void>;
  title: string;
  subtitle: string;
  buttonText: string;
  pageCall: {
    category: string;
    name: string;
  };
  isTriggered?: boolean;
  hideButton?: boolean;
  hideCloseButton?: boolean;
  openDelay?: number;
}

export default function CannotCompleteVerificationDialog({
  onUnemployedClick,
  onManualEvaluationClick,
  title,
  subtitle,
  buttonText,
  pageCall,
  isTriggered,
  hideButton,
  hideCloseButton,
  openDelay,
}: CannotCompleteVerificationDialogProps) {
  const [loadingButton1, setLoadingButton1] = React.useState(false);
  const [loadingButton2, setLoadingButton2] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [errorExternalInformation, setErrorExternalInformation] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const theme = useTheme();

  React.useEffect(() => {
    if (isTriggered) {
      if (openDelay) {
        setTimeout(() => setOpen(true), openDelay);
      } else {
        setOpen(true);
      }
    }
  }, [isTriggered]);

  const handleAction = async (
    functionToExecute: () => Promise<void>,
    setLoading: (loading: boolean) => void,
  ) => {
    setLoading(true);
    try {
      await functionToExecute();
    } catch (e) {
      if (e === 509) {
        analytics.track('ERROR DE FUENTE INFORMACION EXTERNA', {
          initiator: 'backend',
          errorCode: e,
        });
        setErrorExternalInformation(true);
      } else {
        analytics.track('ERROR', {
          initiator: 'backend',
          errorCode: e,
        });
        setError(true);
      }
    }
    setLoading(false);
  };
  const handleUnemployedClick = async () => {
    analytics.track('Click Boton', {
      text: 'Estoy desempleado',
      initiator: 'usuario',
    });
    await handleAction(onUnemployedClick, setLoadingButton1);
  };
  const handleManualEvaluationClick = async () => {
    analytics.track('Click Boton', {
      text: 'Prefiero que me evalúen manualmente',
      initiator: 'usuario',
    });
    await handleAction(onManualEvaluationClick, setLoadingButton2);
  };
  const loading = loadingButton1 || loadingButton2;
  return (
    <AlertDialog
      button={!hideButton ? (
        <Button
          data-pw="cu-2fa-can-not-complete-button"
          variant="text"
          sx={{ height: 'auto', width: 'auto' }}
        >
          <Typography variant="caption" color="primary" align="center" sx={{ textDecoration: 'underline', fontStyle: 'italic' }}>
            {buttonText}
          </Typography>
        </Button>
      ) : undefined}
      isTriggered={open}
      hideSubmitButton
      maxWidth="xs"
      hideLogo
      pageCall={pageCall}
      hideCloseButton={loading || hideCloseButton}
      dialogContentStyle={{ paddingBottom: 7.5 }}
      dialogTitle={(
        <Typography variant="h6" align="center" fontWeight="bold" gutterBottom sx={{ marginBlock: 2.5 }}>
          {title}
        </Typography>
      )}
      dialogContent={(
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, marginTop: 2,
        }}
        >
          <Typography variant="body2" align="center" gutterBottom>
            {subtitle}
          </Typography>
          <Button
            data-pw="cu-2fa-unemployed-button"
            onClick={handleUnemployedClick}
            variant="outlined"
            color="secondary"
            sx={{ width: '100%' }}
            disabled={loading}
          >
            {!loadingButton1 ? 'Estoy desempleado' : <CircularProgress size={15} />}
          </Button>
          <Button
            data-pw="cu-2fa-manual-evaluation-button"
            onClick={handleManualEvaluationClick}
            variant="outlined"
            color="secondary"
            sx={{ width: '100%' }}
            disabled={loading}
          >
            {!loadingButton2 ? 'Prefiero que me evalúen manualmente' : <CircularProgress size={15} />}
          </Button>
          { error && <Typography variant="caption" color="error" align="center" gutterBottom>Ha ocurrido un error</Typography> }
          { errorExternalInformation
           && (
           <Typography variant="caption" color="error" align="center" gutterBottom>
             <div>
               <b>
                 No hemos podido procesar tu evaluación debido a que uno de nuestros
                 {' '}
                 proveedores de información se encuentra temporalmente fuera de servicio.
               </b>
               {' '}
               Por favor vuelve más tarde para que podamos
               {' '}
               <b style={{ color: theme.palette.secondary.main }}>
                 evaluarte oportunamente.
               </b>
             </div>
           </Typography>
           ) }
        </Box>
      )}
    />
  );
}

CannotCompleteVerificationDialog.defaultProps = {
  isTriggered: false,
  hideButton: false,
  hideCloseButton: false,
  openDelay: undefined,
};
